import axios from 'axios';
import { notifyClockStatusChanged, subscribeClockStatusChanged } from '../utils/ClockStatusBus';
import { isSessionExpiredStatus } from '../utils/sessionExpiredHandler';

/**
 * In-call time-tracking clock (workspaces only).
 *
 * Talks to the SAME endpoints the dashboard TimeTracker.vue uses:
 *   GET  /clock-in   -> current status (array; non-empty => an active session)
 *   POST /clock-in   -> { workspace_id }      starts a session, returns timesheet
 *   POST /clock-out  -> { timesheet_id, notes } stops the active session
 *
 * Fully isolated from the websocket pipeline. Every network call is wrapped so a
 * failure can never throw out of the room bundle.
 */

function csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]');
    return el ? el.getAttribute('content') : '';
}

// Convert a clock_in_ts (unix seconds, unix ms, or a date string) to unix seconds.
// Mirrors the defensive behaviour of TimeTracker.vue's toUnixSeconds helper so the
// timer only ever counts forward, regardless of unit/shape quirks.
function toUnixSeconds(ts) {
    if (ts === null || ts === undefined || ts === '') return null;
    if (typeof ts === 'number') {
        return ts > 1e12 ? ts / 1000 : ts;
    }
    const asNumber = Number(ts);
    if (!Number.isNaN(asNumber) && String(ts).trim() !== '') {
        return asNumber > 1e12 ? asNumber / 1000 : asNumber;
    }
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? null : parsed / 1000;
}

function formatTime(seconds) {
    const safe = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
    const h = Math.floor(safe / 3600);
    const m = Math.floor((safe % 3600) / 60);
    const s = Math.floor(safe % 60);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export default class Clock {
    constructor() {
        this.activeClock = null;
        this.interval = null;
        this.baselineElapsedSeconds = 0;
        this.tickAnchorMs = 0;
        this.busy = false;
        this.clockSyncBound = false;
        this.unsubscribeClockStatus = null;
        this.refreshOnFocus = null;
        this.refreshOnVisibility = null;
    }

    cacheElements() {
        this.section = $('#room-clock-section');
        this.statusText = $('#room-clock-status');
        this.timeText = $('#room-clock-time');
        this.workspaceEl = $('#room-clock-workspace');
        this.workspaceNameEl = $('#room-clock-workspace-name');
        this.toggleBtn = $('#room-clock-toggle');
    }

    // No-op unless the workspace-only section was server-rendered into the menu.
    init() {
        this.cacheElements();
        if (!this.section || this.section.length === 0) {
            return;
        }
        this.bind();
        this.bindClockSync();
        this.fetchStatus(false);
    }

    bind() {
        const self = this;
        this.toggleBtn.off('click.roomClock').on('click.roomClock', function () {
            if (self.busy) return;
            if (self.activeClock) {
                self.clockOut();
            } else {
                self.clockIn();
            }
        });

        $('#optionsBtn').off('click.roomClockRefresh').on('click.roomClockRefresh', function () {
            self.fetchStatus(false);
        });
    }

    bindClockSync() {
        if (this.clockSyncBound) {
            return;
        }

        const self = this;
        this.clockSyncBound = true;
        this.unsubscribeClockStatus = subscribeClockStatusChanged((clock) => {
            self.setActiveClock(clock, false);
        });
        this.refreshOnFocus = () => self.fetchStatus(false);
        this.refreshOnVisibility = () => {
            if (document.visibilityState === 'visible') {
                self.fetchStatus(false);
            }
        };

        window.addEventListener('focus', this.refreshOnFocus);
        document.addEventListener('visibilitychange', this.refreshOnVisibility);
    }

    fetchStatus(emit = false) {
        const self = this;
        axios
            .get('/clock-in')
            .then((res) => {
                const data = res && res.data;
                if (data && data.error) {
                    // No ooo_clock_in permission -> hide the whole section.
                    self.section.addClass('d-none');
                    return;
                }
                self.section.removeClass('d-none');
                if (Array.isArray(data) && data.length > 0) {
                    self.setActiveClock(data[0], emit);
                } else {
                    self.setActiveClock(null, emit);
                }
            })
            .catch(() => {
                // Anything unexpected -> hide rather than show a broken control.
                if (self.section) self.section.addClass('d-none');
            });
    }

    currentWorkspaceId() {
        try {
            if (window.kinoRoom && typeof window.kinoRoom.getName === 'function') {
                return window.kinoRoom.getName();
            }
            if (typeof ROOM_NAME !== 'undefined') {
                return ROOM_NAME;
            }
        } catch (e) {
            /* ignore */
        }
        return null;
    }

    clockIn() {
        const self = this;
        const workspaceId = this.currentWorkspaceId();
        if (!workspaceId) {
            self.showError('Unable to determine the current workspace.');
            return;
        }
        self.busy = true;
        self.toggleBtn.prop('disabled', true);
        axios
            .post(
                '/clock-in',
                { workspace_id: workspaceId },
                { headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken(), 'X-Requested-With': 'XMLHttpRequest' } }
            )
            .then((res) => {
                self.setActiveClock(res && res.data, true);
            })
            .catch((err) => {
                if (isSessionExpiredStatus(err)) {
                    return;
                }
                const msg = (err && err.response && err.response.data && err.response.data.message) || 'Unable to clock in. Please try again.';
                self.showError(msg);
            })
            .finally(() => {
                self.busy = false;
                self.toggleBtn.prop('disabled', false);
            });
    }

    clockOut() {
        const self = this;
        if (!this.activeClock || !this.activeClock.id) {
            self.setActiveClock(null);
            return;
        }
        self.busy = true;
        self.toggleBtn.prop('disabled', true);
        axios
            .post(
                '/clock-out',
                { timesheet_id: this.activeClock.id, notes: null },
                { headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken(), 'X-Requested-With': 'XMLHttpRequest' } }
            )
            .then(() => {
                self.setActiveClock(null, true);
            })
            .catch((err) => {
                if (isSessionExpiredStatus(err)) {
                    return;
                }
                const msg = (err && err.response && err.response.data && err.response.data.message) || 'Unable to clock out. Please try again.';
                self.showError(msg);
            })
            .finally(() => {
                self.busy = false;
                self.toggleBtn.prop('disabled', false);
            });
    }

    setActiveClock(clock, emit = true) {
        clearInterval(this.interval);
        const validClock = clock && !clock.error && clock.id ? clock : null;

        if (validClock) {
            this.activeClock = validClock;
            const clockInSeconds = toUnixSeconds(validClock.clock_in_ts);
            const nowSeconds = Date.now() / 1000;
            this.baselineElapsedSeconds = clockInSeconds != null ? Math.max(0, nowSeconds - clockInSeconds) : 0;
            this.tickAnchorMs = Date.now();
            this.statusText.text('Clocked in');
            this.section.addClass('is-clocked-in');
            if (validClock.workspace_name) {
                this.workspaceNameEl.text(validClock.workspace_name);
                this.workspaceEl.removeClass('d-none');
            } else {
                this.workspaceEl.addClass('d-none');
            }
            this.toggleBtn.text('Clock Out').addClass('is-stop');
            this.timeText.text(formatTime(this.baselineElapsedSeconds)).removeClass('d-none');
            this.startTimer();
        } else {
            this.activeClock = null;
            this.baselineElapsedSeconds = 0;
            this.tickAnchorMs = 0;
            this.statusText.text('Not clocked in');
            this.section.removeClass('is-clocked-in');
            this.timeText.text('00:00:00').addClass('d-none');
            this.workspaceEl.addClass('d-none');
            this.toggleBtn.text('Clock In').removeClass('is-stop');
        }

        if (emit) {
            notifyClockStatusChanged(validClock);
        }
    }

    startTimer() {
        const self = this;
        clearInterval(this.interval);
        this.interval = setInterval(() => {
            if (!self.activeClock) return;
            const localDelta = (Date.now() - self.tickAnchorMs) / 1000;
            self.timeText.text(formatTime(self.baselineElapsedSeconds + localDelta));
        }, 1000);
    }

    showError(msg) {
        try {
            if (typeof Video !== 'undefined' && Video && typeof Video.showNotification === 'function') {
                Video.showNotification('<i class="fas fa-exclamation-triangle"></i>', msg, 'error');
                return;
            }
        } catch (e) {
            /* fall through */
        }
        console.warn('[Kino Clock]', msg);
    }
}
